'use client';

/* ------------------------------------------------------------------
   ShareNavContext — the "second space" coordinator.

   Each share owner (Key Ratings, factor chapters, X-Ray) drives its
   modal's open state from a single shared `activeSection`, so the nav
   strip inside FactorShareModal can jump between sections without each
   modal living its own disconnected life.
   ------------------------------------------------------------------ */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface ShareNavSection {
  id: string;
  label: string;
  /** Viewable by non-premium users. Others are paywalled. */
  free: boolean;
}

/** Order = order in the nav strip. Only sections backed by FactorShareModal. */
export const SHARE_NAV_SECTIONS: ShareNavSection[] = [
  { id: 'key-ratings', label: 'Key Ratings', free: true },
  { id: 'growth', label: 'Growth', free: true },
  { id: 'value', label: 'Value', free: false },
  { id: 'quality', label: 'Quality', free: false },
  { id: 'dividends', label: 'Dividends & Buybacks', free: false },
  // Free like its chapter on the page (FinancialsTab FREE_FACTOR_IDS).
  { id: 'momentum', label: 'Momentum', free: true },
  { id: 'xray', label: '16 charts', free: false },
  { id: 'verdict', label: 'Verdict', free: false },
];

interface ShareNavValue {
  activeSection: string | null;
  openSection: (id: string) => void;
  close: () => void;
}

const ShareNavCtx = createContext<ShareNavValue | null>(null);

export function ShareNavProvider({ children }: { children: React.ReactNode }) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const openSection = useCallback((id: string) => setActiveSection(id), []);
  const close = useCallback(() => setActiveSection(null), []);
  const value = useMemo(
    () => ({ activeSection, openSection, close }),
    [activeSection, openSection, close],
  );
  return <ShareNavCtx.Provider value={value}>{children}</ShareNavCtx.Provider>;
}

/** Non-throwing: radar / heatmap share modals render outside this provider. */
export function useShareNav(): ShareNavValue | null {
  return useContext(ShareNavCtx);
}
